import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import swal from "sweetalert";

export default function VendorMessages() {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [subject, setSubject] = useState("");
  const [newMsgOpen, setNewMsgOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const activeConversationRef = useRef(activeConversation);
  activeConversationRef.current = activeConversation;

  const fetchMessages = useCallback(async (convId) => {
    try {
      const res = await axios.get(`/api/vendor/messages/${convId}`);
      setMessages(res.data?.messages || []);
      await axios.put(`/api/vendor/messages/${convId}/read`).catch(() => {});
    } catch (err) {
      console.warn("Could not load messages:", err.message);
    }
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get("/api/vendor/messages/conversations");
      const convList = res.data?.conversations || [];
      setConversations(convList);
      if (convList.length > 0 && !activeConversationRef.current) {
        setActiveConversation(convList[0]);
        fetchMessages(convList[0]._id);
      }
    } catch (err) {
      console.warn("Could not load vendor conversations:", err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchMessages]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const handleSelectConv = (conv) => {
    setActiveConversation(conv);
    fetchMessages(conv._id);
  };

  const handleSendReply = async (e) => {
    if (e) e.preventDefault();
    if (!replyText.trim()) return;

    try {
      setSending(true);
      await axios.post("/api/vendor/messages/send", {
        conversationId: activeConversation?._id,
        message: replyText.trim(),
      });
      setReplyText("");
      if (activeConversation) {
        fetchMessages(activeConversation._id);
      }
      fetchConversations();
    } catch (err) {
      swal({
        title: "Send Failed",
        text: err.response?.data?.msg || "Could not send message.",
        icon: "error",
      });
    } finally {
      setSending(false);
    }
  };

  const handleCreateNewMessage = async (e) => {
    if (e) e.preventDefault();
    if (!replyText.trim()) return;

    try {
      setSending(true);
      await axios.post("/api/vendor/messages/send", {
        subject: subject.trim(),
        message: replyText.trim(),
      });
      swal({ title: "Message Sent", text: "Your message has been sent to Marketplace Support.", icon: "success" });
      setReplyText("");
      setSubject("");
      setNewMsgOpen(false);
      fetchConversations();
    } catch (err) {
      swal({
        title: "Send Failed",
        text: err.response?.data?.msg || "Could not send message.",
        icon: "error",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1100px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <div>
          <h1 style={{ fontSize: "22px", fontWeight: 900, color: "#0f172a", margin: 0 }}>
            Messages &amp; Admin Support
          </h1>
          <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#64748b" }}>
            Direct communication with Marketplace Admin and Support team
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setSubject("");
            setReplyText("");
            setNewMsgOpen(true);
          }}
          style={{ fontWeight: 700, fontSize: "13px" }}
        >
          ✉ Compose Message
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px", color: "#64748b" }}>
          Loading messages...
        </div>
      ) : conversations.length === 0 ? (
        <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "60px", textAlign: "center" }}>
          <h3 style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a", marginBottom: "8px" }}>
            No Messages Yet
          </h3>
          <p style={{ fontSize: "13px", color: "#64748b", maxWidth: "420px", margin: "0 auto 20px" }}>
            Need help with product listing, verification, or order compliance? Reach out directly to Marketplace Admin.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setNewMsgOpen(true)}
            style={{ fontWeight: 700 }}
          >
            ✉ Send Message to Admin
          </button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: "20px", minHeight: "500px", height: "calc(100vh - 220px)" }}>
          {/* Threads list */}
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "14px 16px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
              <strong style={{ fontSize: "13px", color: "#334155" }}>
                Conversations ({conversations.length})
              </strong>
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {conversations.map((conv) => (
                <div
                  key={conv._id}
                  onClick={() => handleSelectConv(conv)}
                  style={{
                    padding: "14px 16px",
                    borderBottom: "1px solid #f1f5f9",
                    cursor: "pointer",
                    background: activeConversation?._id === conv._id ? "#f0fdf4" : "#ffffff",
                    borderLeft: activeConversation?._id === conv._id ? "3px solid #16a34a" : "3px solid transparent",
                    transition: "all 0.15s",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <strong style={{ fontSize: "13.5px", color: "#0f172a" }}>
                      Marketplace Admin
                    </strong>
                    {conv.vendorUnreadCount > 0 && (
                      <span style={{ background: "#dc2626", color: "#fff", fontSize: "11px", fontWeight: 800, padding: "2px 7px", borderRadius: "99px" }}>
                        {conv.vendorUnreadCount}
                      </span>
                    )}
                  </div>
                  <p style={{ margin: "4px 0 0", fontSize: "12.5px", color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {conv.lastMessage || "No messages"}
                  </p>
                  <span style={{ fontSize: "11px", color: "#94a3b8", display: "block", marginTop: "4px" }}>
                    {new Date(conv.lastMessageAt || conv.createdAt).toLocaleDateString("en-IN", { dateStyle: "medium" })}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Messages panel */}
          {activeConversation ? (
            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <div style={{ padding: "14px 20px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
                <strong style={{ fontSize: "14px", color: "#0f172a" }}>
                  Marketplace Administrator
                </strong>
                <span style={{ display: "block", fontSize: "12px", color: "#64748b" }}>
                  Official Platform Support
                </span>
              </div>

              <div style={{ flex: 1, overflowY: "auto", padding: "18px", display: "flex", flexDirection: "column", gap: "12px" }}>
                {messages.map((msg) => (
                  <div
                    key={msg._id}
                    style={{
                      alignSelf: msg.senderRole === "vendor" ? "flex-end" : "flex-start",
                      maxWidth: "75%",
                      background: msg.senderRole === "vendor" ? "#1e40af" : "#f1f5f9",
                      color: msg.senderRole === "vendor" ? "#ffffff" : "#0f172a",
                      borderRadius: "14px",
                      padding: "12px 16px",
                      fontSize: "13.5px",
                      lineHeight: 1.5,
                    }}
                  >
                    {msg.subject && (
                      <strong style={{ display: "block", marginBottom: "4px", fontSize: "12.5px" }}>
                        {msg.subject}
                      </strong>
                    )}
                    <p style={{ margin: 0 }}>{msg.message}</p>
                    <span style={{ fontSize: "10.5px", opacity: 0.75, display: "block", marginTop: "6px", textAlign: "right" }}>
                      {new Date(msg.createdAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                ))}
              </div>

              <form onSubmit={handleSendReply} style={{ borderTop: "1px solid #e2e8f0", padding: "14px 18px", display: "flex", gap: "10px" }}>
                <input
                  type="text"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type a message to admin..."
                  style={{ flex: 1, padding: "10px 14px", border: "1.5px solid #cbd5e1", borderRadius: "8px", fontSize: "13.5px", outline: "none" }}
                />
                <button
                  type="submit"
                  disabled={sending || !replyText.trim()}
                  className="btn btn-primary"
                  style={{ padding: "10px 22px", fontWeight: 700, borderRadius: "8px" }}
                >
                  {sending ? "Sending..." : "Send"}
                </button>
              </form>
            </div>
          ) : (
            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b" }}>
              Select a conversation to view messages.
            </div>
          )}
        </div>
      )}

      {/* Compose modal */}
      {newMsgOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.65)", backdropFilter: "blur(4px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
          <div style={{ background: "#ffffff", borderRadius: "16px", maxWidth: "520px", width: "100%", padding: "26px", border: "1px solid #e2e8f0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #f1f5f9", paddingBottom: "14px", marginBottom: "16px" }}>
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 900, color: "#0f172a" }}>
                ✉ Message Marketplace Admin
              </h3>
              <button
                type="button"
                onClick={() => setNewMsgOpen(false)}
                style={{ background: "#f1f5f9", border: "none", borderRadius: "8px", width: "32px", height: "32px", cursor: "pointer", fontWeight: 700 }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateNewMessage}>
              <div style={{ marginBottom: "14px" }}>
                <label style={{ display: "block", fontSize: "12.5px", fontWeight: 800, color: "#334155", marginBottom: "6px" }}>
                  Subject
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g., Question about product listing approval"
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13px", outline: "none" }}
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "12.5px", fontWeight: 800, color: "#334155", marginBottom: "6px" }}>
                  Message *
                </label>
                <textarea
                  rows={4}
                  required
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Describe your inquiry or request in detail..."
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13px", outline: "none" }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  onClick={() => setNewMsgOpen(false)}
                  disabled={sending}
                  className="btn btn-outline-secondary"
                  style={{ padding: "8px 18px", fontSize: "13px" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sending || !replyText.trim()}
                  className="btn btn-primary"
                  style={{ padding: "8px 22px", fontSize: "13px", fontWeight: 700 }}
                >
                  {sending ? "Sending..." : "Send Message"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
